/**
 * Przeciągnięcie karty zadania na tablicy Kanban = zwykły zapis rekordu, na tych samych zasadach co
 * formularz (`TaskDetail`). Cała logika żyje we wspólnym haku `useRecordKanbanMove`; tu tylko
 * konfiguracja zadań: `PATCH /api/tasks/:id`, prawo edycji zadań, rdzenne pola z dedykowaną
 * kolumną (firma, kategoria) i komunikat odrzucenia wspólny z formularzem (`taskSaveErrorMessage`).
 *
 * Status i priorytet to pola wyboru z surową wartością w kolumnie — po nich karty da się przeciągać;
 * osoba odpowiedzialna, kontrahent, typ i autor to odwołania (tablica jest wtedy tylko do podglądu).
 */

import { useMemo } from 'react';

import { TASK_EXCLUDE_SLUGS } from '@/components/tasks/taskListColumns';
import type { SystemModuleFieldDef } from '@/lib/ext-list-columns';
import {
  type RecordKanbanMove,
  type RecordKanbanMoveConfig,
  useRecordKanbanMove,
} from '@/lib/records/useRecordKanbanMove';
import { TASK_PERMISSION } from '@/lib/tasks/task-api';
import { taskSaveErrorMessage } from '@/lib/tasks/task-save-errors';
import { useTaskNumberingFieldLabel } from '@/lib/tasks/useTaskNumberingBaseFieldOptions';

/** Rdzenne pola zadania z dedykowaną kolumną — wykluczone z kolumn katalogu, albo kategoria. */
function hasDedicatedColumn(slug: string): boolean {
  return TASK_EXCLUDE_SLUGS.has(slug) || slug === 'category_id';
}

export type TaskKanbanMove = RecordKanbanMove;

export function useTaskKanbanMove(opts: {
  fields: readonly SystemModuleFieldDef[];
  /** Po udanym zapisie (odświeżenie listy). */
  onSaved: () => void;
}): TaskKanbanMove {
  const numberingFieldLabel = useTaskNumberingFieldLabel();
  const config = useMemo<RecordKanbanMoveConfig>(
    () => ({
      savePath: (id) => `/tasks/${id}`,
      editPermission: TASK_PERMISSION.edit,
      hasDedicatedColumn,
      saveErrorMessage: (err, t, fieldLabel) =>
        taskSaveErrorMessage(err, t, fieldLabel, numberingFieldLabel),
      fallbackToastKey: 'tenant.tasks.detail.toast.saveError',
    }),
    [numberingFieldLabel],
  );
  return useRecordKanbanMove(config, opts);
}
